import { ProductDetailsResponse } from "../types/products";
import { PackageDimensions } from "../types/shipping";

// Helper type for flexible product data
type FlexibleProductData = ProductDetailsResponse | Record<string, unknown>;

// Type guard helpers
const hasProperty = (
  obj: unknown,
  prop: string,
): obj is Record<string, unknown> => {
  return (
    typeof obj === "object" &&
    obj !== null &&
    prop in obj &&
    typeof (obj as Record<string, unknown>)[prop] !== "undefined"
  );
};

const getStringProperty = (
  obj: FlexibleProductData,
  prop: string,
): string | undefined => {
  if (hasProperty(obj, prop)) {
    const value = obj[prop];
    return typeof value === "string" ? value : undefined;
  }
  return undefined;
};
export const extractCategoryFromProduct = (
  productData: FlexibleProductData,
): string => {
  if (
    hasProperty(productData, "categories") &&
    Array.isArray(productData.categories) &&
    productData.categories.length > 0
  ) {
    // ✅ Get MOST SPECIFIC category (last one)
    const category = productData.categories[productData.categories.length - 1];

    if (typeof category === "string") {
      return category;
    }
  }

  const category = getStringProperty(productData, "category");
  if (category) return category;

  const productCategory = getStringProperty(productData, "product_category");
  if (productCategory) return productCategory;

  // Try to infer category from product title
  const title = getStringProperty(productData, "title");
  if (title) {
    const titleLower = title.toLowerCase();

    // Electronics
    if (
      titleLower.includes("iphone") ||
      titleLower.includes("samsung") ||
      titleLower.includes("phone") ||
      titleLower.includes("laptop") ||
      titleLower.includes("macbook") ||
      titleLower.includes("computer") ||
      titleLower.includes("tablet") ||
      titleLower.includes("ipad") ||
      titleLower.includes("watch") ||
      titleLower.includes("headphones") ||
      titleLower.includes("earbuds") ||
      titleLower.includes("speaker")
    ) {
      return "Electronics";
    }

    // Clothing
    if (
      titleLower.includes("shirt") ||
      titleLower.includes("dress") ||
      titleLower.includes("jeans") ||
      titleLower.includes("sneakers") ||
      titleLower.includes("shoes") ||
      titleLower.includes("jacket") ||
      titleLower.includes("suit") ||
      titleLower.includes("wear") ||
      titleLower.includes("clothing")
    ) {
      return "Clothing";
    }

    // Books
    if (
      titleLower.includes("book") ||
      titleLower.includes("novel") ||
      titleLower.includes("textbook") ||
      titleLower.includes("dvd") ||
      titleLower.includes("blu-ray") ||
      titleLower.includes("cd")
    ) {
      return "Books";
    }

    // Vitamins/Health
    if (
      titleLower.includes("vitamin") ||
      titleLower.includes("supplement") ||
      titleLower.includes("protein") ||
      titleLower.includes("shampoo") ||
      titleLower.includes("toothpaste") ||
      titleLower.includes("soap")
    ) {
      return "Vitamins";
    }

    // Home & Garden
    if (
      titleLower.includes("coffee maker") ||
      titleLower.includes("blender") ||
      titleLower.includes("microwave") ||
      titleLower.includes("refrigerator") ||
      titleLower.includes("dishwasher") ||
      titleLower.includes("vacuum")
    ) {
      return "Home & Garden";
    }

    // Sports
    if (
      titleLower.includes("basketball") ||
      titleLower.includes("football") ||
      titleLower.includes("soccer") ||
      titleLower.includes("tennis") ||
      titleLower.includes("golf") ||
      titleLower.includes("yoga")
    ) {
      return "Sports & Outdoors";
    }

    // Toys
    if (
      titleLower.includes("toy") ||
      titleLower.includes("game") ||
      titleLower.includes("puzzle") ||
      titleLower.includes("lego") ||
      titleLower.includes("doll") ||
      titleLower.includes("action figure")
    ) {
      return "Toys & Games";
    }
  }

  // Default fallback
  return "Electronics";
};

const getNestedProperty = (obj: FlexibleProductData, prop: string): unknown => {
  if (hasProperty(obj, prop)) {
    return obj[prop];
  }
  return undefined;
};

const getStringOrNumber = (value: unknown): string | number | undefined => {
  if (typeof value === "string" || typeof value === "number") {
    return value;
  }
  return undefined;
};

export const extractPackageDimensionsFromProduct = (
  productData: FlexibleProductData,
): PackageDimensions | null => {
  // Try to extract package dimensions from various possible locations
  if (hasProperty(productData, "package_dimensions")) {
    const dims = productData.package_dimensions;
    if (
      typeof dims === "object" &&
      dims !== null &&
      ("size" in dims || "weight" in dims)
    ) {
      return dims as PackageDimensions;
    }
  }

  if (hasProperty(productData, "dimensions")) {
    const dims = productData.dimensions;
    if (
      typeof dims === "object" &&
      dims !== null &&
      ("size" in dims || "weight" in dims)
    ) {
      return dims as PackageDimensions;
    }
  }

  if (hasProperty(productData, "shipping_dimensions")) {
    const dims = productData.shipping_dimensions;
    if (
      typeof dims === "object" &&
      dims !== null &&
      ("size" in dims || "weight" in dims)
    ) {
      return dims as PackageDimensions;
    }
  }

  // Try to extract from specifications
  if (hasProperty(productData, "specifications")) {
    const specs = productData.specifications;
    if (typeof specs === "object" && specs !== null) {
      const specsObj = specs as Record<string, unknown>;

      // Look for weight information
      const weight =
        getStringOrNumber(specsObj.weight) ||
        getStringOrNumber(specsObj.shipping_weight) ||
        getStringOrNumber(specsObj.package_weight);
      const width =
        getStringOrNumber(specsObj.width) ||
        getStringOrNumber(specsObj.package_width);
      const length =
        getStringOrNumber(specsObj.length) ||
        getStringOrNumber(specsObj.package_length);
      const depth =
        getStringOrNumber(specsObj.depth) ||
        getStringOrNumber(specsObj.package_depth) ||
        getStringOrNumber(specsObj.height) ||
        getStringOrNumber(specsObj.package_height);

      if (weight || (width && length && depth)) {
        return {
          size: {
            width: width
              ? { amount: parseFloat(String(width)), unit: "inches" }
              : undefined,
            length: length
              ? { amount: parseFloat(String(length)), unit: "inches" }
              : undefined,
            depth: depth
              ? { amount: parseFloat(String(depth)), unit: "inches" }
              : undefined,
          },
          weight: weight
            ? { amount: parseFloat(String(weight)), unit: "pounds" }
            : undefined,
        };
      }
    }
  }

  // Try to extract from shipping information
  const shippingWeight = getStringOrNumber(
    getNestedProperty(productData, "shipping_weight"),
  );
  if (shippingWeight) {
    return {
      weight: {
        amount: parseFloat(String(shippingWeight)),
        unit: "pounds",
      },
    };
  }

  // If no dimensions found, return null
  return null;
};

export const hasValidPackageDimensions = (
  dimensions: PackageDimensions | null,
): boolean => {
  if (!dimensions) return false;

  // Check if we have at least weight information
  if (dimensions.weight && dimensions.weight.amount > 0) {
    return true;
  }

  // Check if we have size information
  if (dimensions.size) {
    const { width, length, depth } = dimensions.size;
    if (
      width?.amount &&
      width.amount > 0 &&
      length?.amount &&
      length.amount > 0 &&
      depth?.amount &&
      depth.amount > 0
    ) {
      return true;
    }
  }

  return false;
};

/**
 * Extracts estimated weight in kg from product data
 * @param product - Product data
 * @returns Weight in kg, or undefined if not available
 */
export const extractEstimatedWeightInKg = (
  product: ProductDetailsResponse | Record<string, unknown>,
): number | undefined => {
  // Try to get weight from package_dimensions
  if (hasProperty(product, "package_dimensions")) {
    const packageDimensions = product.package_dimensions;
    if (
      hasProperty(packageDimensions, "weight") &&
      typeof packageDimensions.weight === "object" &&
      packageDimensions.weight !== null
    ) {
      const weight = packageDimensions.weight as {
        amount?: number;
        unit?: string;
      };
      if (weight.amount && weight.amount > 0) {
        // Convert to kg based on unit
        const unit = (weight.unit || "").toLowerCase();
        if (
          unit.includes("kg") ||
          unit === "kilogram" ||
          unit === "kilograms"
        ) {
          return weight.amount;
        } else if (
          unit.includes("lb") ||
          unit === "pound" ||
          unit === "pounds"
        ) {
          return weight.amount * 0.453592; // Convert pounds to kg
        } else if (
          unit.includes("oz") ||
          unit === "ounce" ||
          unit === "ounces"
        ) {
          return weight.amount * 0.0283495; // Convert ounces to kg
        } else {
          // Default to pounds if unit is unknown
          return weight.amount * 0.453592;
        }
      }
    }
  }

  // Try to get weight from shipping_weight string (e.g., "1.2 lbs")
  if (hasProperty(product, "shipping_weight")) {
    const shippingWeight = product.shipping_weight;
    if (typeof shippingWeight === "string") {
      // Parse string like "1.2 lbs" or "0.5 kg"
      const match = shippingWeight.match(
        /(\d+\.?\d*)\s*(lb|pound|pounds|kg|kilogram|kilograms|oz|ounce|ounces)/i,
      );
      if (match) {
        const amount = parseFloat(match[1]);
        const unit = match[2].toLowerCase();
        if (unit.includes("kg")) {
          return amount;
        } else if (unit.includes("lb") || unit.includes("pound")) {
          return amount * 0.453592;
        } else if (unit.includes("oz") || unit.includes("ounce")) {
          return amount * 0.0283495;
        }
      } else {
        // Try to parse as number (assume pounds)
        const num = parseFloat(shippingWeight);
        if (!isNaN(num) && num > 0) {
          return num * 0.453592;
        }
      }
    } else if (typeof shippingWeight === "number" && shippingWeight > 0) {
      // Assume pounds if it's a number
      return shippingWeight * 0.453592;
    }
  }

  return undefined;
};
